import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { PermissionKey } from '@raptor-fe/core';
import { ApplicationContextStore } from '../application-context/application-context.store';
import { RouterService } from '../router-service/router.service';
import { NamedRoutes } from '../router-service/route.constants';

/**
 * Builds a route guard that admits navigation only when the current user holds at least one of the
 * required permission keys (any-of semantics). Pass the route's `:view` feature key(s). The guard
 * waits for the effective permission set to load before deciding so navigation is not denied during
 * the initial asynchronous load, and redirects to the home route when the permission is missing.
 *
 * Admin and system roles project to the full catalog server-side, so privileged users always pass.
 * Permission `:view` gating is feature visibility only; the backend remains authoritative for data
 * access, so a guard configured with no keys imposes no restriction.
 *
 * @param requiredPermissions The permission key, or any-of set of keys, required to enter the route.
 * @returns A {@link CanActivateFn} enforcing the permission requirement.
 *
 * @example
 * ```ts
 * { path: 'role', canActivate: [hasPermissionGuard(Permissions.Role.View)], loadChildren: ... }
 * ```
 */
export const hasPermissionGuard = (...requiredPermissions: Array<PermissionKey | string>): CanActivateFn => {
    return () => {
        const store = inject(ApplicationContextStore);
        const routerService = inject(RouterService);

        const evaluate = (): boolean => {
            const granted = requiredPermissions.length === 0 || store.hasAnyPermission(requiredPermissions);
            if (!granted) {
                routerService.navigateAndReplaceUrl(NamedRoutes.landing());
            }
            return granted;
        };

        if (store.permissionsLoaded()) {
            return evaluate();
        }

        return toObservable(store.permissionsLoaded).pipe(
            filter((loaded) => loaded),
            take(1),
            map(() => evaluate()),
        );
    };
};
